import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import octopusInstance from "../../services/octopus/octopusInstance";
import {
  addTriggeredAlert,
  incrementUnseenAlerts,
  fetchAlertsAction,
} from "../../store/actions/alerts";

/**
 * Subscribes to triggered-alert push messages and feeds them into Redux.
 * Mirrors the structure of useSocketMessageUpdates (auth gating, retry,
 * visibility reconnect). On each notification:
 *   - dedupes by id against alerts already seen in this session
 *   - dispatches ADD_TRIGGERED_ALERT + INCREMENT_UNSEEN_ALERTS
 *   - refreshes the active-alerts list (the fired alert moves from
 *     running -> triggered server-side)
 *   - surfaces a notistack toast so the user notices it anywhere in the app
 */
function useAlertUpdates() {
  const auth = useSelector((state) => state.auth);
  const triggeredInRedux = useSelector((state) => state.alerts?.triggeredAlerts) || [];
  const dispatch = useDispatch();

  const handlerRef = useRef(null);
  const setupAttempts = useRef(0);
  const displayedAlerts = useRef(new Set());

  // Re-read latest Redux state from a ref inside the long-lived socket
  // callback so we don't have to tear down the subscription on each update.
  const triggeredRef = useRef(triggeredInRedux);
  triggeredRef.current = triggeredInRedux;

  const handleAlertPacket = (packet) => {
    const alertData = packet?.data || packet;
    if (!alertData) return;

    const id = alertData.alert_id || alertData.id;
    if (!id || displayedAlerts.current.has(id)) return;

    // Dedupe ONLY against the triggered list / session Set. The alert id
    // matching an ACTIVE alert is expected (the alert that fired is still
    // active server-side until status flips) — we want to process it.
    const knownInTriggered = triggeredRef.current.some((a) => (a.id || a.alert_id) === id);
    if (knownInTriggered) {
      displayedAlerts.current.add(id);
      return;
    }
    displayedAlerts.current.add(id);

    const normalized = {
      ...alertData,
      id,
      update_entry_time: alertData.update_entry_time || new Date().toISOString(),
    };

    dispatch(addTriggeredAlert(normalized));
    dispatch(incrementUnseenAlerts());
    // The fired alert is no longer running upstream; refresh the active list.
    dispatch(fetchAlertsAction());

    enqueueSnackbar(
      normalized.user_message || `Price alert triggered for ${normalized.trading_symbol || ''}`.trim(),
      { variant: "info", autoHideDuration: 5000 }
    );
  };

  const setupHandler = () => {
    try {
      const clientId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");
      if (!clientId || !authToken) return;

      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }

      // Mirrors captur_web_app's subscribeOctopusUpdate({ messageType: "Notifications",
      // payload: [{ clientId }] }) — the wire frame ships on the "updates"
      // channel (hydra-topic-config code 65) and topic callbacks are keyed
      // by "Notifications".
      handlerRef.current = octopusInstance.wsHandler({
        messageType: "Notifications",
        subscriptionLocation: "app-alerts",
        payload: [{ clientId }],
      });

      if (handlerRef.current) {
        handlerRef.current.subscribe(({ msg }) => {
          // msg can be the packet directly or { packet }; handle both.
          handleAlertPacket(msg?.packet ? msg.packet : msg);
        });
      }
    } catch (_e) {
      // swallow — retry loop below will re-attempt
    }
  };

  // Setup when auth state flips to logged in.
  useEffect(() => {
    if (auth?.isLoggedIn) setupHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isLoggedIn, dispatch]);

  // Initial attempt + bounded retry, plus cleanup on unmount.
  useEffect(() => {
    const attempt = () => {
      const authToken = localStorage.getItem("auth_token");
      const clientId = localStorage.getItem("login_id");
      if (authToken && clientId && !handlerRef.current && setupAttempts.current < 3) {
        setupAttempts.current += 1;
        setupHandler();
      }
    };

    attempt();
    const retryTimer = setInterval(() => {
      if (!handlerRef.current && setupAttempts.current < 3) attempt();
    }, 2000);

    return () => {
      clearInterval(retryTimer);
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Reconnect when the tab becomes visible again.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        setupAttempts.current = 0;
        setupHandler();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return null;
}

export default useAlertUpdates;
